import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircleRounded,
  ContentCopyRounded,
  OpenInNewRounded,
  StorefrontRounded,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { PRODUCT_IMAGES } from '../../branding/assets';
import {
  exchangeIfoodAuthorization,
  getCoreConfigEditor,
  getFeedbackIfoodSyncStatus,
  getIfoodImportStatus,
  listIfoodMerchants,
  saveCoreConfigEditor,
  selectIfoodMerchant,
  startIfoodAuthorization,
  syncFeedbackIfoodReviews,
} from '../../services/api';
import { completeInitialSetup } from '../../services/initialSetup';
import { getIfoodErrorMessage } from '../../utils/ifoodError';
import IfoodLoadingBackdrop from '../common/IfoodLoadingBackdrop';
import IfoodPromptImportDialog from '../Products/Atendabot/components/configuration/IfoodPromptImportDialog.tsx';
import IfoodCatalogImportDialog from '../Products/Cardapio/components/IfoodCatalogImportDialog';
import {
  cloneScheduleDays,
  normalizeEditorResponse,
  normalizeScheduleDays,
} from '../settings/settingsUtils';

const IFOOD_RED = '#EA1D2C';

const IfoodLogo = ({ size = 26 }) => (
  <Box component="svg" role="img" aria-label="iFood" viewBox="0 0 24 24" sx={{ width: size, height: size }}>
    <path
      fill="currentColor"
      d="M8.428 1.67c-4.65 0-7.184 4.149-7.184 6.998 0 2.294 2.2 3.299 4.25 3.299l-.006-.006c4.244 0 7.184-3.854 7.184-6.998 0-2.29-2.175-3.293-4.244-3.293zm11.328 0c-4.65 0-7.184 4.149-7.184 6.998 0 2.294 2.2 3.299 4.25 3.299l-.006-.006C21.061 11.96 24 8.107 24 4.963c0-2.29-2.18-3.293-4.244-3.293zM14.172 14.52l2.435 1.834c-2.17 2.07-6.124 3.525-9.353 3.17A8.913 8.913 0 01.23 14.541H0a9.598 9.598 0 008.828 7.758c3.814.24 7.323-.905 9.947-3.13l-.004.007 1.08 2.988 1.555-7.623-7.234-.02Z"
    />
  </Box>
);

const ProductBadge = ({ image, name, background }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: 2.25,
        bgcolor: background,
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box component="img" src={image} alt="" sx={{ width: '92%', height: '92%', objectFit: 'contain' }} />
    </Box>
    <Typography variant="body2" fontWeight={850} color="text.primary">{name}</Typography>
  </Stack>
);

const SetupStep = ({ number, title, description, product, done, disabled, children }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 3,
      borderColor: done ? 'success.light' : 'divider',
      opacity: disabled ? 0.62 : 1,
      boxShadow: done ? '0 12px 30px -24px rgba(22, 163, 74, .7)' : 'none',
    }}
  >
    <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: '50%',
              bgcolor: done ? 'success.main' : '#0f172a',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              fontWeight: 900,
              flexShrink: 0,
            }}
          >
            {done ? <CheckCircleRounded fontSize="small" /> : number}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={900}>{title}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, maxWidth: 560 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
        {product}
      </Stack>
      <Box sx={{ mt: 2.25 }}>{children}</Box>
    </CardContent>
  </Card>
);

const mapIfoodAddress = (current, address) => ({
  ...current,
  nome: current?.nome || 'Unidade principal',
  logradouro: address?.street || current?.logradouro || '',
  numero: address?.number || current?.numero || '',
  complemento: address?.complement || current?.complemento || '',
  bairro: address?.district || current?.bairro || '',
  cidade: address?.city || current?.cidade || '',
  estado: String(address?.state || current?.estado || '').toUpperCase(),
  cep: address?.postal_code || current?.cep || '',
  latitude: address?.latitude ?? current?.latitude ?? '',
  longitude: address?.longitude ?? current?.longitude ?? '',
});

const InitialSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const callbackExchangeRef = useRef('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [status, setStatus] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [authorization, setAuthorization] = useState(null);
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [coreConfig, setCoreConfig] = useState(null);
  const [feedbackState, setFeedbackState] = useState(null);
  const [catalogDone, setCatalogDone] = useState(false);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [feedbackResult, setFeedbackResult] = useState(null);

  const connected = Boolean(status?.connected);
  const merchantId = selectedMerchantId || status?.merchant?.id || '';
  const merchant = useMemo(() => (
    merchants.find((item) => String(item.id) === String(merchantId))
    || status?.merchant
    || null
  ), [merchantId, merchants, status?.merchant]);
  const storeDone = Boolean(
    coreConfig?.has_principal_address_setup && coreConfig?.has_open_schedule
  );
  const feedbackDone = Boolean(feedbackState?.enabled);
  const completedSteps = Number(connected && Boolean(merchantId))
    + Number(storeDone)
    + Number(catalogDone)
    + Number(feedbackDone);

  const loadMerchants = useCallback(async () => {
    const response = await listIfoodMerchants({ page: 1, size: 100 });
    const rows = Array.isArray(response?.merchants) ? response.merchants : [];
    setMerchants(rows);
    return rows;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextStatus, nextCoreConfig, nextFeedbackState] = await Promise.all([
        getIfoodImportStatus(),
        getCoreConfigEditor(),
        getFeedbackIfoodSyncStatus(),
      ]);
      setStatus(nextStatus);
      setCoreConfig(nextCoreConfig);
      setFeedbackState(nextFeedbackState);
      setSelectedMerchantId(nextStatus?.merchant?.id || '');
      setAuthorization(nextStatus?.user_code ? {
        user_code: nextStatus.user_code,
        verification_url: nextStatus.verification_url,
        verification_url_complete: nextStatus.verification_url_complete,
      } : null);
      if (nextStatus?.connected) await loadMerchants();
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível carregar a configuração inicial.'));
    } finally {
      setLoading(false);
    }
  }, [loadMerchants]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (!merchantId || typeof window === 'undefined') {
      setCatalogDone(false);
      return;
    }
    setCatalogDone(
      window.localStorage.getItem(`foodstation:initial-setup:catalog:${merchantId}`) === 'done'
    );
  }, [merchantId]);

  const completeAuthorization = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return;
    setBusy('Conectando sua conta ao iFood…');
    try {
      const nextStatus = await exchangeIfoodAuthorization(code);
      const rows = Array.isArray(nextStatus?.merchants) ? nextStatus.merchants : [];
      setStatus(nextStatus);
      setMerchants(rows);
      setSelectedMerchantId(nextStatus?.merchant?.id || (rows.length === 1 ? rows[0].id : ''));
      setAuthorization(null);
      setAuthorizationCode('');
      if (!rows.length) await loadMerchants();
      toast.success('iFood conectado com sucesso.');
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível concluir a conexão com o iFood.'));
    } finally {
      setBusy('');
    }
  }, [loadMerchants]);

  useEffect(() => {
    const code = searchParams.get('authorizationCode')
      || searchParams.get('authorization_code')
      || searchParams.get('code');
    if (!code || callbackExchangeRef.current === code) return;
    callbackExchangeRef.current = code;
    void completeAuthorization(code).finally(() => {
      const next = new URLSearchParams(searchParams);
      ['authorizationCode', 'authorization_code', 'code', 'merchantId', 'merchant_id']
        .forEach((key) => next.delete(key));
      setSearchParams(next, { replace: true });
    });
  }, [completeAuthorization, searchParams, setSearchParams]);

  const handleConnect = async () => {
    const partnerWindow = window.open('', '_blank');
    setBusy('Abrindo o Portal do iFood…');
    try {
      const response = await startIfoodAuthorization();
      setStatus(response);
      setAuthorization(response?.authorization || null);
      const url = response?.authorization?.verification_url_complete
        || response?.authorization?.verification_url;
      if (!url) throw new Error('O iFood não informou o endereço de autorização.');
      if (partnerWindow) {
        partnerWindow.opener = null;
        partnerWindow.location.href = url;
      } else {
        window.location.assign(url);
      }
    } catch (error) {
      partnerWindow?.close();
      toast.error(getIfoodErrorMessage(error, 'Não foi possível abrir o Portal do iFood.'));
    } finally {
      setBusy('');
    }
  };

  const handleCopyActivationCode = async () => {
    try {
      await navigator.clipboard.writeText(authorization?.user_code || '');
      toast.success('Código copiado.');
    } catch (error) {
      toast.info('Não foi possível copiar automaticamente. Selecione o código e copie.');
    }
  };

  const handleSelectMerchant = async (event) => {
    const nextMerchantId = event.target.value;
    setSelectedMerchantId(nextMerchantId);
    setBusy('Selecionando a loja…');
    try {
      const nextStatus = await selectIfoodMerchant(nextMerchantId);
      setStatus(nextStatus);
      toast.success('Loja selecionada.');
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível selecionar esta loja.'));
    } finally {
      setBusy('');
    }
  };

  const handleStoreImport = async ({ address, scheduleDays }) => {
    setBusy('Salvando endereço e horários…');
    try {
      const editor = await getCoreConfigEditor();
      const normalized = normalizeEditorResponse(editor || {});
      const principalAddress = address
        ? mapIfoodAddress(normalized.principalAddress, address)
        : normalized.principalAddress;
      const principalSchedule = Array.isArray(scheduleDays)
        ? normalizeScheduleDays(scheduleDays)
        : cloneScheduleDays(
          normalized.unitSchedules.find((unit) => unit.is_principal)?.schedule_days || []
        );
      const unitSchedules = normalized.unitSchedules.map((unit) => ({
        address_id: unit.address_id || null,
        address_nome: unit.is_principal
          ? principalAddress.nome || 'Unidade principal'
          : unit.address_nome,
        is_principal: Boolean(unit.is_principal),
        use_principal_schedule: unit.is_principal ? true : Boolean(unit.use_principal_schedule),
        schedule_days: unit.is_principal || unit.use_principal_schedule
          ? cloneScheduleDays(principalSchedule)
          : cloneScheduleDays(unit.schedule_days),
      }));

      const savedCoreConfig = await saveCoreConfigEditor({
        principal_address: principalAddress,
        additional_addresses: normalized.additionalAddresses,
        schedule_days: principalSchedule,
        unit_schedules: unitSchedules,
      });
      setCoreConfig(savedCoreConfig);
      toast.success('Endereço e horários salvos para uso no AtendaBot.');
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível salvar os dados da loja.'));
    } finally {
      setBusy('');
    }
  };

  const handleFeedbackImport = async () => {
    setBusy('Trazendo avaliações para o FeedbackHero…');
    try {
      const result = await syncFeedbackIfoodReviews({
        mode: feedbackState?.enabled && feedbackState?.last_review_created_at ? 'incremental' : 'full',
        force: true,
      });
      setFeedbackResult(result);
      setFeedbackState(result?.state || await getFeedbackIfoodSyncStatus());
      const count = Number(result?.received_reviews_count ?? result?.imported ?? 0);
      toast.success(count
        ? `${count} avaliação(ões) recebida(s) no FeedbackHero.`
        : 'Consulta concluída. O iFood não retornou avaliações nesse período.');
    } catch (error) {
      toast.error(getIfoodErrorMessage(error, 'Não foi possível trazer as avaliações do iFood.'));
    } finally {
      setBusy('');
    }
  };

  const handleFinish = () => {
    completeInitialSetup();
    navigate('/products', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7f6', py: { xs: 2, md: 5 } }}>
      <IfoodLoadingBackdrop open={Boolean(busy)} message={busy || 'Aguarde…'} />
      <Container maxWidth="md">
        <Card sx={{ borderRadius: 4, mb: 2.5, overflow: 'hidden', boxShadow: '0 22px 60px -40px rgba(15,23,42,.55)' }}>
          <Box sx={{ px: { xs: 2.5, sm: 4 }, py: { xs: 3, sm: 4 }, bgcolor: '#102c21', color: '#fff' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Box sx={{ width: 58, height: 58, borderRadius: 3, bgcolor: IFOOD_RED, display: 'grid', placeItems: 'center' }}>
                <IfoodLogo size={34} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h4" fontWeight={950}>Prepare seu restaurante</Typography>
                <Typography sx={{ mt: 0.75, color: '#cfe3d8' }}>
                  Conecte o iFood e aproveite os dados que você já cadastrou por lá.
                </Typography>
              </Box>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(completedSteps / 4) * 100}
              sx={{ mt: 3, height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,.16)' }}
            />
            <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#cfe3d8' }}>
              {completedSteps} de 4 etapas concluídas
            </Typography>
          </Box>
        </Card>

        {loading ? (
          <Stack alignItems="center" spacing={2} sx={{ py: 10 }}>
            <CircularProgress />
            <Typography color="text.secondary">Preparando sua configuração…</Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <SetupStep
              number={1}
              title="Conecte sua conta do iFood"
              description="Você autoriza o acesso no Portal do Parceiro. Sua senha nunca é informada à FoodStation."
              done={connected && Boolean(merchantId)}
              product={<Stack direction="row" spacing={1} alignItems="center" color={IFOOD_RED}><IfoodLogo /><Typography fontWeight={900}>iFood</Typography></Stack>}
            >
              {!connected ? (
                <Stack spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleConnect}
                    startIcon={<OpenInNewRounded />}
                    sx={{ alignSelf: 'flex-start', bgcolor: IFOOD_RED, textTransform: 'none', fontWeight: 850, borderRadius: 2.5, '&:hover': { bgcolor: '#c91524' } }}
                  >
                    Abrir Portal do iFood
                  </Button>
                  {authorization?.user_code && (
                    <Alert
                      severity="info"
                      sx={{
                        width: '100%',
                        borderRadius: 2.5,
                        boxSizing: 'border-box',
                        '& .MuiAlert-message': {
                          width: '100%',
                          minWidth: 0,
                        },
                      }}
                    >
                      <Stack spacing={1.5} sx={{ width: '100%' }}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ width: '100%' }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption">Código de ativação</Typography>
                            <Typography variant="h6" fontFamily="monospace" fontWeight={900}>{authorization.user_code}</Typography>
                          </Box>
                          <Button
                            size="small"
                            startIcon={<ContentCopyRounded />}
                            onClick={handleCopyActivationCode}
                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                          >
                            Copiar código
                          </Button>
                        </Stack>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1}
                          alignItems="stretch"
                          sx={{ width: '100%' }}
                        >
                          <TextField
                            size="small"
                            fullWidth
                            label="Código de autorização do iFood"
                            value={authorizationCode}
                            onChange={(event) => setAuthorizationCode(event.target.value)}
                            sx={{ flex: '1 1 auto', minWidth: 0 }}
                          />
                          <Button
                            variant="contained"
                            disabled={!authorizationCode.trim()}
                            onClick={() => completeAuthorization(authorizationCode)}
                            sx={{
                              minWidth: { md: 190 },
                              px: 3,
                              flexShrink: 0,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Concluir conexão
                          </Button>
                        </Stack>
                      </Stack>
                    </Alert>
                  )}
                </Stack>
              ) : (
                <Stack spacing={1.5}>
                  <Alert severity="success" sx={{ borderRadius: 2.5 }}>
                    Conta conectada{merchant?.name ? ` · ${merchant.name}` : ''}
                  </Alert>
                  {merchants.length > 1 && (
                    <TextField select label="Loja que deseja configurar" value={merchantId} onChange={handleSelectMerchant}>
                      {merchants.map((item) => <MenuItem key={item.id} value={item.id}>{item.name || item.corporateName || item.id}</MenuItem>)}
                    </TextField>
                  )}
                </Stack>
              )}
            </SetupStep>

            <SetupStep
              number={2}
              title="Traga os dados da loja"
              description="Copie endereço e horários para deixar o atendimento pronto mais rápido."
              done={storeDone}
              disabled={!merchantId}
              product={<ProductBadge image={PRODUCT_IMAGES.atendabot} name="AtendaBot" background="#3127a0" />}
            >
              <Button variant="outlined" disabled={!merchantId} onClick={() => setStoreDialogOpen(true)} sx={{ textTransform: 'none', fontWeight: 850, borderRadius: 2.5 }}>
                {storeDone ? 'Atualizar dados da loja' : 'Trazer endereço e horários'}
              </Button>
            </SetupStep>

            <SetupStep
              number={3}
              title="Traga seu cardápio"
              description="Escolha os produtos do iFood que deseja usar no seu cardápio digital."
              done={catalogDone}
              disabled={!merchantId}
              product={<ProductBadge image={PRODUCT_IMAGES.oneMenu} name="OneMenu" background="#fff5ed" />}
            >
              <Button variant="outlined" disabled={!merchantId} onClick={() => setCatalogDialogOpen(true)} sx={{ textTransform: 'none', fontWeight: 850, borderRadius: 2.5 }}>
                {catalogDone ? 'Atualizar cardápio' : 'Escolher produtos'}
              </Button>
            </SetupStep>

            <SetupStep
              number={4}
              title="Traga suas avaliações"
              description="As avaliações disponíveis no iFood passam a aparecer no painel de feedbacks."
              done={feedbackDone}
              disabled={!merchantId}
              product={<ProductBadge image={PRODUCT_IMAGES.feedbackHero} name="FeedbackHero" background="#143023" />}
            >
              <Stack spacing={1.25} alignItems="flex-start">
                <Button variant="outlined" disabled={!merchantId} onClick={handleFeedbackImport} sx={{ textTransform: 'none', fontWeight: 850, borderRadius: 2.5 }}>
                  {feedbackDone ? 'Atualizar avaliações' : 'Trazer avaliações'}
                </Button>
                {feedbackResult && (
                  <Typography variant="caption" color="text.secondary">
                    {Number(feedbackResult?.received_reviews_count ?? feedbackResult?.imported ?? 0)} avaliação(ões) retornada(s) pelo iFood.
                  </Typography>
                )}
              </Stack>
            </SetupStep>

            <Divider />
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={1.5} justifyContent="space-between" sx={{ pb: 3 }}>
              <Button onClick={handleFinish} color="inherit" sx={{ textTransform: 'none' }}>Fazer depois</Button>
              <Button variant="contained" onClick={handleFinish} endIcon={<StorefrontRounded />} sx={{ textTransform: 'none', fontWeight: 900, borderRadius: 2.5, bgcolor: '#102c21', '&:hover': { bgcolor: '#1b4937' } }}>
                Ir para meus produtos
              </Button>
            </Stack>
          </Stack>
        )}
      </Container>

      <IfoodPromptImportDialog
        open={storeDialogOpen}
        merchantId={merchantId}
        merchantName={merchant?.name}
        onClose={() => setStoreDialogOpen(false)}
        onApply={handleStoreImport}
        onReconnect={() => navigate('/configuracoes/ifood')}
      />
      <IfoodCatalogImportDialog
        open={catalogDialogOpen}
        merchantId={merchantId}
        onClose={() => setCatalogDialogOpen(false)}
        onImported={() => {
          setCatalogDone(true);
          if (typeof window !== 'undefined' && merchantId) {
            window.localStorage.setItem(`foodstation:initial-setup:catalog:${merchantId}`, 'done');
          }
        }}
      />
    </Box>
  );
};

export default InitialSetupPage;
